/**
 * Header notification bell.
 *
 * Shows the signed-in user's push notifications across EVERY app (Hub, Task, HR, …). The inbox is
 * device-scoped, so requesting it without an appId returns everything delivered to any of their
 * devices. Each row carries the sending app's icon (from the Application Catalogue), its title and
 * message; "View" opens the full notification — image and timestamps included.
 */

import { useEffect, useState } from 'react';
import { Indicator, Popover } from '@mantine/core';
import {
  IconBell,
  IconBellOff,
  IconRefresh,
  IconChecks,
  IconApps,
  IconX,
  IconClock,
  IconSend,
  IconCheck,
  IconEye,
  IconAlertCircle,
} from '@tabler/icons-react';
import {
  ActionIcon,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Avatar,
  Divider,
  Modal,
  Paper,
  ThemeIcon,
  Box,
  Image,
  Loader,
  Alert,
} from '@/components/ui';
import { Tooltip } from '@/components/ui/overlays';
import { notifications } from '@mantine/notifications';
import { onForegroundMessage } from '@/utils/firebase';
import { useNotificationInbox, type InboxApp, type InboxItem } from '../hooks/useNotificationInbox';
import type { NotificationInboxItem } from '@actschurch/ccms-sdk';

/** The backend stores audit stamps as a yyyyMMddHHmmss decimal, not an epoch. */
function parseStamp(value?: number | null): Date | undefined {
  if (!value) return undefined;
  const s = String(value);
  if (s.length !== 14) return undefined;
  const d = new Date(
    Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)),
    Number(s.slice(8, 10)), Number(s.slice(10, 12)), Number(s.slice(12, 14)),
  );
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function timeAgo(date?: Date): string {
  if (!date) return '';
  const secs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function AppIcon({ app, size = 'lg' }: { app: InboxApp; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  return (
    <Avatar src={app.iconUrl || undefined} radius="sm" size={size}>
      <IconApps size={20} />
    </Avatar>
  );
}

/** Status → a colour that means something: delivered is good, failed is not. */
function statusColor(status?: string | null): string {
  const v = (status || '').toLowerCase();
  if (v.includes('fail') || v.includes('error')) return 'red';
  if (v.includes('deliver') || v.includes('read')) return 'teal';
  if (v.includes('sent')) return 'green';
  if (v.includes('process')) return 'blue';
  if (v.includes('pending') || v.includes('queue')) return 'yellow';
  return 'gray';
}

/** Short form so several steps fit side by side, e.g. "14 Jul, 00:21". */
function shortWhen(date: Date): string {
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** One step of the delivery trail, as a column in a horizontal stepper. */
function TimelineStep({
  icon,
  label,
  date,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  date: Date;
  color: string;
}) {
  return (
    <Stack gap={4} align="center" style={{ minWidth: 96 }}>
      <ThemeIcon size={30} radius="xl" variant="light" color={color}>
        {icon}
      </ThemeIcon>
      <Text size="xs" fw={600}>{label}</Text>
      <Text size="xs" c="dimmed" ta="center" style={{ whiteSpace: 'nowrap' }}>
        {shortWhen(date)}
      </Text>
    </Stack>
  );
}

function DetailModal({
  item,
  app,
  opened,
  onClose,
}: {
  item: NotificationInboxItem | null;
  app: InboxApp | null;
  opened: boolean;
  onClose: () => void;
}) {
  if (!item || !app) return null;

  const created = parseStamp(item.createdDateTime);
  const sent = item.sentAt ? new Date(item.sentAt) : undefined;
  const delivered = item.deliveredAt ? new Date(item.deliveredAt) : undefined;
  const read = item.readAt ? new Date(item.readAt) : undefined;

  const status = item.status || 'Unknown';
  const color = statusColor(status);
  const failed = color === 'red';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      centered
      radius="md"
      padding="lg"
      withCloseButton={false}
    >
      <Stack gap="lg">
        {/* Header: who it came from, when, and how it ended up — the three things you look for first. */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <AppIcon app={app} size="md" />
            <Stack gap={0} style={{ minWidth: 0 }}>
              {app.name && <Text size="sm" fw={600} lineClamp={1}>{app.name}</Text>}
              <Text size="xs" c="dimmed">{timeAgo(created)}</Text>
            </Stack>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Badge size="sm" variant="light" color={color}>{status}</Badge>
            <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label="Close">
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* The image is the point of a rich push — give it the width. */}
        {item.imageUrl && (
          <Image src={item.imageUrl} radius="md" fit="cover" mah={260} />
        )}

        <Stack gap={6}>
          <Text fz="lg" fw={700} style={{ lineHeight: 1.3 }}>
            {item.title || '(No title)'}
          </Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {item.body || '(No message)'}
          </Text>
        </Stack>

        {failed && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} p="xs">
            <Text size="xs">
              This notification failed to deliver, so it may never have reached the device.
            </Text>
          </Alert>
        )}

        <Divider label="Delivery" labelPosition="left" />

        <Paper withBorder radius="md" p="sm">
          {/* Horizontal trail. Only steps that actually happened are shown, joined by connectors; it
              scrolls within itself so a long trail never makes the modal scroll sideways. */}
          <div style={{ overflowX: 'auto' }}>
            <Group gap={0} wrap="nowrap" align="flex-start" justify="center" style={{ minWidth: 'min-content' }}>
              {(
                [
                  { key: 'queued', icon: <IconClock size={14} />, label: 'Queued', date: created, color: 'gray' },
                  { key: 'sent', icon: <IconSend size={14} />, label: 'Sent', date: sent, color: failed ? 'red' : 'green' },
                  { key: 'delivered', icon: <IconCheck size={14} />, label: 'Delivered', date: delivered, color: 'teal' },
                  { key: 'read', icon: <IconEye size={14} />, label: 'Read', date: read, color: 'indigo' },
                ].filter((st) => !!st.date) as {
                  key: string; icon: React.ReactNode; label: string; date: Date; color: string;
                }[]
              ).map((st, i) => (
                <Group key={st.key} gap={0} wrap="nowrap" align="flex-start">
                  {i > 0 && (
                    <Box
                      style={{
                        width: 28,
                        height: 2,
                        marginTop: 14,
                        background: 'var(--mantine-color-gray-3)',
                      }}
                    />
                  )}
                  <TimelineStep icon={st.icon} label={st.label} date={st.date} color={st.color} />
                </Group>
              ))}
            </Group>
          </div>
        </Paper>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>#{item.messageId}</Text>
          <Button variant="light" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default function NotificationBell() {
  const { items, unreadCount, loading, error, refresh, markRead, markAllRead, appFor } =
    useNotificationInbox();

  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState<NotificationInboxItem | null>(null);

  // Live push while the console is OPEN.
  //
  // FCM only shows an OS notification itself when the tab is in the BACKGROUND (the service worker's
  // onBackgroundMessage). A focused tab delivers to onMessage in the page instead and displays
  // nothing — so without this handler a push that FCM accepted looks like it never arrived.
  useEffect(() => {
    let active = true;
    onForegroundMessage((payload) => {
      if (!active) return;
      const n = payload.notification;
      notifications.show({
        title: n?.title ?? 'Notification',
        message: n?.body ?? '',
        color: 'indigo',
      });
      refresh(); // pull the new row into the bell + badge
    }).catch(() => {
      /* Firebase not configured in this environment — history still works, just no live toast. */
    });
    return () => { active = false; };
  }, [refresh]);

  const openDetail = (item: InboxItem) => {
    setSelected(item);
    setOpened(false);
    // Opening a notification is what "reading" it means — clear EVERY per-device copy, or the
    // hidden duplicates stay unread and the badge comes back on the next poll.
    if (!item.readAt) markRead(item.duplicateIds);
  };

  return (
    <>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-end"
        width={460}
        shadow="md"
        withArrow
      >
        <Popover.Target>
          <Tooltip label="Notifications" position="bottom">
            <Indicator
              inline
              label={unreadCount > 99 ? '99+' : unreadCount}
              size={16}
              disabled={unreadCount === 0}
              color="red"
              offset={7}
            >
              {/* Never disabled — with nothing unread you still want to read the history. */}
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="Notifications"
                onClick={() => {
                  const next = !opened;
                  setOpened(next);
                  if (next) refresh();
                }}
              >
                <IconBell size={18} />
              </ActionIcon>
            </Indicator>
          </Tooltip>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Group justify="space-between" p="sm" pb="xs">
            <Group gap="xs">
              <Text fw={600} size="sm">Notifications</Text>
              {unreadCount > 0 && <Badge size="sm" color="red" variant="light">{unreadCount} new</Badge>}
            </Group>
            <Group gap={4}>
              <ActionIcon variant="subtle" size="sm" onClick={refresh} title="Refresh" loading={loading}>
                <IconRefresh size={15} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={markAllRead}
                title="Mark all as read"
                disabled={unreadCount === 0}
              >
                <IconChecks size={15} />
              </ActionIcon>
            </Group>
          </Group>

          <Divider />

          {error ? (
            <Alert color="red" m="sm">{error}</Alert>
          ) : loading && items.length === 0 ? (
            <Group justify="center" p="lg"><Loader size="sm" /></Group>
          ) : items.length === 0 ? (
            <Stack align="center" gap={6} p="lg">
              <IconBellOff size={28} opacity={0.4} />
              <Text size="sm" c="dimmed">No notifications yet</Text>
            </Stack>
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <Stack gap={0}>
                {items.map((item) => {
                  const app = appFor(item.appId);
                  const created = parseStamp(item.createdDateTime);
                  const isUnread = !item.readAt;

                  return (
                    <div key={item.messageId}>
                      <Group
                        gap="sm"
                        p="sm"
                        wrap="nowrap"
                        align="flex-start"
                        style={{
                          cursor: 'pointer',
                          background: isUnread ? 'var(--mantine-color-blue-light)' : undefined,
                        }}
                        onClick={() => openDetail(item)}
                      >
                        <AppIcon app={app} />

                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          {/*
                            Unknown app: drop the app line entirely AND move the timestamp onto the title
                            row, so the title rises to the top instead of sitting under an empty gap.
                          */}
                          {app.name && (
                            <Group gap={6} wrap="nowrap" justify="space-between">
                              <Text size="xs" c="dimmed" lineClamp={1}>{app.name}</Text>
                              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{timeAgo(created)}</Text>
                            </Group>
                          )}

                          <Group gap={6} wrap="nowrap" justify="space-between" align="baseline">
                            <Text
                              size="sm"
                              fw={isUnread ? 600 : 500}
                              lineClamp={1}
                              style={{ flex: 1, minWidth: 0 }}
                            >
                              {item.title || '(No title)'}
                            </Text>
                            {!app.name && (
                              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{timeAgo(created)}</Text>
                            )}
                          </Group>
                          {/* One line only — the full message lives in the detail popup. */}
                          <Group gap={6} wrap="nowrap">
                            <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                              {item.body}
                            </Text>
                            {item.imageUrl && <Badge size="xs" variant="light" color="grape">Image</Badge>}
                          </Group>
                        </Stack>

                      </Group>
                      <Divider />
                    </div>
                  );
                })}
              </Stack>
            </div>
          )}
        </Popover.Dropdown>
      </Popover>

      <DetailModal
        item={selected}
        app={selected ? appFor(selected.appId) : null}
        opened={!!selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
